import React, { useState, createContext, useContext, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../Utils/api";
import { timesheetStatus } from "../../Utils/constants";

const apiPath = {
  fetchTimesheetDetails: "/timesheet/gettimesheetdetails",
  checkPinRequired: "/timesheet/link/ispinrequired",
  verifyPin: "/timesheet/link/verify",
  approveTimesheet: "/timesheet/link/approvetimesheet",
  rejectTimesheet: "/timesheet/link/rejecttimesheet",
};

const nullFn = () => null;
const PublicTimesheetContext = createContext({
  loading: true,
  timesheets: null,
  timesheetMaster: null,
  days: [],
  approveTimesheet: nullFn,
  rejectTimesheet: nullFn,
  isPinRequired: nullFn,
  verifyPin: nullFn,
});

export const usePublicTimesheetProvider = () =>
  useContext(PublicTimesheetContext);

const PublicTimesheetProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [timesheets, setTimesheets] = useState(null);
  const [days, setDays] = useState([]);
  const [timesheetMaster, setTimesheetMaster] = useState(null);

  useEffect(() => {
    if (timesheets && days.length === 0)
      fetchTimesheetDetails(timesheets.map((t) => t.id));
  }, [timesheets]);

  const approveTimesheet = async (timesheet_id, comments, email) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.approveTimesheet, {
        timesheet_id,
        comments,
        email,
      });

      if (res.status === 200 && res.data?.success) {
        const temp = timesheets.map((t) => ({
          ...t,
          status: timesheetStatus.approved,
        }));

        setTimesheets(temp);
      } else {
        throw new Error("Error occurred while approving timesheet");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const rejectTimesheet = async (timesheet_id, comments, email) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.rejectTimesheet, {
        timesheet_id,
        comments,
        email,
      });

      if (res.status === 200 && res.data?.success) {
        const temp = timesheets.map((t) => ({
          ...t,
          status: timesheetStatus.rejected,
        }));

        setTimesheets(temp);
      } else {
        throw new Error("Error occurred while approving timesheet");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const isPinRequired = async (linkId) => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.checkPinRequired, {
        params: { id: linkId },
      });

      if (res.status === 200 && res.data?.success) {
        setLoading(false);

        // Array of timesheets, length depends on the number of weeks
        if (res.data.timesheet?.length) setTimesheets(res.data.timesheet);
        if (res.data.timesheet_id)
          setTimesheetMaster({ id: res.data.timesheet_id });

        return res.data.result;
      } else {
        throw new Error("Error occurred while approving timesheet");
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      return null;
    }
  };

  const verifyPin = async (linkId, pin) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.verifyPin, { linkId, pin });
      if (res.status === 200 && res.data?.success) {
        setLoading(false);

        // Array of timesheets, length depends on the number of weeks
        if (res.data.timesheet?.length) setTimesheets(res.data.timesheet);
        if (res.data.timesheet_id)
          setTimesheetMaster({ id: res.data.timesheet_id });

        return res.data.result;
      } else {
        throw new Error("Error occurred while approving timesheet");
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      return null;
    }
  };

  const fetchTimesheetDetails = async (timesheets) => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchTimesheetDetails, {
        params: { timesheet_id: timesheets },
      });

      if (res.status === 200 && res.data?.success) {
        let hours = 0,
          minutes = 0;
        res.data.timesheet?.forEach((t) => {
          hours += t.hours;
          minutes += t.minutes;
        });

        if (minutes >= 60) {
          hours += Math.floor(minutes / 60);
          minutes = minutes % 60;
        }

        setTimesheetMaster({
          ...timesheetMaster,
          hours,
          minutes,
          consultant: res.data.timesheet?.[0]?.consultant,
          job: res.data.timesheet?.[0]?.job,
        });

        setDays(res.data.result);
        setLoading(false);
      } else {
        throw new Error("Error occurred while fetching timesheet details");
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      Swal.fire("Error", "Error occurred while fetching timesheet", "error");
    }
  };

  const value = {
    loading,
    timesheets,
    timesheetMaster,
    days,
    approveTimesheet,
    rejectTimesheet,
    isPinRequired,
    verifyPin,
  };

  return (
    <PublicTimesheetContext.Provider value={value}>
      {children}
    </PublicTimesheetContext.Provider>
  );
};

export default PublicTimesheetProvider;
